import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { PeriodOption } from '@/presentation/hooks/useGlobalPeriod';

interface PeriodSelectProps {
  value: string;
  options: PeriodOption[];
  onChange: (value: string) => void;
  id?: string;
  className?: string;
}

/** Chip de mes/año (y vista anual) para el header del shell. */
export function PeriodSelect({ value, options, onChange, id = 'global-period', className = '' }: PeriodSelectProps) {
  const { t } = useTranslation();
  const groups = useMemo(() => {
    const byYear = new Map<number, PeriodOption[]>();
    for (const option of options) {
      const list = byYear.get(option.year) ?? [];
      list.push(option);
      byYear.set(option.year, list);
    }
    return Array.from(byYear.entries());
  }, [options]);

  return (
    <label className={`inline-flex min-w-0 max-w-[10.5rem] items-center gap-1.5 sm:max-w-[15rem] ${className}`}>
      <span className="hidden shrink-0 text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 md:inline">
        {t('period.label')}
      </span>
      <select
        id={id}
        aria-label={t('period.chipAria')}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="min-w-0 flex-1 truncate rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-xs font-semibold text-slate-800 shadow-sm outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 dark:border-gray-600 dark:bg-gray-800 dark:text-slate-100 sm:text-sm"
      >
        {groups.map(([year, yearOptions]) => (
          <optgroup key={year} label={String(year)}>
            {yearOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.granularity === 'year' ? t('period.annual', { year: option.year }) : option.label}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
    </label>
  );
}
